import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { initKvIntro } from '../animation/kv-intro';

gsap.registerPlugin(SplitText);

initKvIntro({
  titleSelector: '.js-awards-title',
  subtitleSelector: '.js-awards-subtitle',
  leadSelector: '.js-awards-lead',
});

/**
 * About Us / Awards & Media (/aboutus/awards/) のタブ切替スクリプト。
 *
 * - WAI-ARIA Authoring Practices "Tabs" パターンに沿った role=tablist / tab / tabpanel
 * - Automatic activation: 矢印キー / Home / End で focus 移動と同時にパネル切替
 *   (パネルは事前 SSR で軽量なため、表示遅延コストがなく Auto 側を採用)
 * - URL hash 同期 (#certifications / #media)、pushState/popstate でブラウザ戻る対応
 * - SR 向け aria-live で「{タブ名} を表示中」をアナウンス
 */

function initAwardsTabs() {
  const tablist = document.querySelector('.p-aboutus-awards__tablist');
  if (!tablist) return;

  const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
  const panels = Array.from(
    document.querySelectorAll('.p-aboutus-awards__panel')
  );
  if (!tabs.length || !panels.length) return;

  const liveRegion = document.querySelector('.p-aboutus-awards__live');

  /**
   * 指定タブをアクティブ化。
   * @param {HTMLElement} targetTab
   * @param {{focus?: boolean, hashMode?: 'push'|'replace'|'none', announce?: boolean}} [opts]
   *   hashMode:
   *     - 'push'    クリック / キー操作起点。履歴を積み、ブラウザ「戻る」で前タブへ戻れる
   *     - 'replace' 初期化や popstate 受信時。履歴を作らない
   *     - 'none'    URL を触らない
   */
  function activate(targetTab, opts = {}) {
    const { focus = true, hashMode = 'push', announce = true } = opts;
    const key = targetTab.dataset.tabKey;

    tabs.forEach(tab => {
      const isActive = tab === targetTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach(panel => {
      const isActive = panel.dataset.tabKey === key;
      panel.hidden = !isActive;
    });

    if (announce && liveRegion) {
      const labelEl = targetTab.querySelector('.p-aboutus-awards__tab-label');
      const label =
        labelEl && labelEl.textContent ? labelEl.textContent.trim() : key;
      // i18n: PHP 側で翻訳済テンプレ ("%s を表示中" 等) を data-attr 経由で受け取り、
      // {label} を埋め込む。data-attr 未設定時はフォールバック表記。
      const tmpl = liveRegion.dataset.announceTemplate || '%s';
      liveRegion.textContent = tmpl.replace('%s', label);
    }

    if (focus) targetTab.focus();

    if (hashMode !== 'none') {
      const hash = `#${key}`;
      if (window.location.hash !== hash) {
        if (hashMode === 'push') {
          window.history.pushState(null, '', hash);
        } else {
          window.history.replaceState(null, '', hash);
        }
      }
    }
  }

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      activate(tab, { focus: true, hashMode: 'push' });
    });
  });

  tablist.addEventListener('keydown', e => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      return;
    }
    const currentIdx = tabs.findIndex(t => t === document.activeElement);
    if (currentIdx === -1) return;

    let nextIdx = currentIdx;
    if (e.key === 'ArrowLeft') {
      nextIdx = (currentIdx - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'ArrowRight') {
      nextIdx = (currentIdx + 1) % tabs.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = tabs.length - 1;
    }
    e.preventDefault();
    activate(tabs[nextIdx], { focus: true, hashMode: 'push' });
  });

  // ブラウザ「戻る」「進む」で URL hash が変わった時に対応タブへ切替。
  // hash 不在状態に戻った場合 (= 履歴で hash が付く前の状態に戻った) は
  // ユーザー意図を尊重して現在のタブを維持し、勝手に tabs[0] へ強制復帰しない。
  window.addEventListener('popstate', () => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const target = tabs.find(t => t.dataset.tabKey === hash);
    if (!target) return;
    activate(target, { focus: false, hashMode: 'none', announce: true });
  });

  // 初期化: URL hash があれば対応タブを開く。なければ最初のタブ (= 受賞/認定)。
  // 不正 hash (#anything-invalid 等) の場合は silently 先頭タブを表示しつつ、
  // 履歴を汚さずに URL 上の hash を消して状態と URL を一貫させる。
  const initHash = window.location.hash.replace(/^#/, '');
  const matchedTab = tabs.find(t => t.dataset.tabKey === initHash);
  const initialTab = matchedTab || tabs[0];
  if (initHash && !matchedTab) {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
  }
  activate(initialTab, {
    focus: false,
    hashMode: 'none',
    announce: false,
  });
}

function initAwardsCursor() {
  if (window.matchMedia('(max-width: 1024px)').matches) return;

  const main = document.querySelector('.p-aboutus-awards');
  const items = document.querySelectorAll(
    '.p-aboutus-awards__item:has(> .p-aboutus-awards__item-link)'
  );
  if (!main || !items.length) return;

  const cursor = document.createElement('div');
  cursor.className = 'p-aboutus-awards__cursor';
  cursor.textContent = 'VIEW MORE';
  main.appendChild(cursor);

  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;
  const LERP = 0.2;

  document.addEventListener('mousemove', e => {
    targetX = e.clientX;
    targetY = e.clientY;
  });

  (function animate() {
    currentX += (targetX - currentX) * LERP;
    currentY += (targetY - currentY) * LERP;
    cursor.style.left = currentX + 'px';
    cursor.style.top = currentY + 'px';
    requestAnimationFrame(animate);
  })();

  items.forEach(item => {
    item.addEventListener('mouseenter', () =>
      cursor.classList.add('is-visible')
    );
    item.addEventListener('mouseleave', () =>
      cursor.classList.remove('is-visible')
    );
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initAwardsTabs();
    initAwardsCursor();
  });
} else {
  initAwardsTabs();
  initAwardsCursor();
}
